import { TrendingUp } from "lucide-react";
import {
    Bar,
    BarChart,
    XAxis,
    YAxis,
    Legend,
    Cell,
    CartesianGrid,
} from "recharts";

import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "@/components/ui/chart";
import { Product } from "@/types/Products";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { calculateProfitUplift } from "@/lib/utils";

type UpliftData = {
    product_name: string;
    uplift: number;
    status: Product["status"];
    fill: string;
};

const chartConfig = {
    optimizing: {
        label: "Optimizing",
        color: "hsl(var(--primary))",
    },
    optimized: {
        label: "Optimized",
        color: "hsl(var(--chart-2))",
    },
} satisfies ChartConfig;

export function ProductUpliftCard() {
    const [upliftData, setUpliftData] = useState<UpliftData[]>([]);
    const { session, isDemoMode } = useAuth();

    useEffect(() => {
        async function fetchProducts() {
            const { data: products } = await supabase
                .from("products")
                .select(
                    "product_name, status, optimization_data, optimization_progress"
                )
                .in("status", ["Optimizing", "Optimized"])
                .limit(20)
                .order("id", { ascending: true });

            if (products) {
                const data: UpliftData[] = products
                    .map((product) => ({
                        product_name:
                            product.product_name.length > 20
                                ? product.product_name.substring(0, 20) + "..."
                                : product.product_name,
                        uplift: Number(
                            (
                                product.optimization_progress?.marketsets?.reduce(
                                    (max: number, marketset: any) => {
                                        const uplift =
                                            calculateProfitUplift(
                                                marketset.data
                                            ) *
                                            100 *
                                            (isDemoMode ? 3 : 1);
                                        return Math.max(max, uplift);
                                    },
                                    0
                                ) || 0
                            ).toFixed(1)
                        ),
                        status: product.status,
                        fill:
                            product.status === "Optimizing"
                                ? chartConfig.optimizing.color
                                : chartConfig.optimized.color,
                    }))
                    .filter((product) => product.uplift !== 0);
                setUpliftData(data);
            }
        }

        fetchProducts();
    }, [session]);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Product Uplift</CardTitle>
                <CardDescription>
                    Showing top products by profit uplift
                </CardDescription>
            </CardHeader>
            <div className="relative">
                {/* <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm">
                    <p className="text-lg font-semibold text-muted-foreground">
                        Feature in development
                    </p>
                </div> */}
                <CardContent>
                    <ChartContainer config={chartConfig}>
                        <BarChart
                            accessibilityLayer
                            data={upliftData}
                            layout="vertical"
                            margin={{
                                left: 20,
                                bottom: 20,
                                right: 20,
                                top: 40,
                            }}
                        >
                            <CartesianGrid horizontal={false} />
                            <YAxis
                                dataKey="product_name"
                                type="category"
                                tickLine={false}
                                axisLine={false}
                                width={100}
                            />
                            <XAxis
                                type="number"
                                tickLine={false}
                                axisLine={false}
                                tickMargin={10}
                            />
                            <ChartTooltip
                                cursor={false}
                                content={
                                    <ChartTooltipContent
                                        formatter={(value) =>
                                            `${value}% uplift`
                                        }
                                    />
                                }
                            />
                            <Bar
                                dataKey="uplift"
                                radius={[0, 12, 12, 0]}
                                fill={undefined}
                                name="uplift"
                            >
                                {upliftData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.fill}
                                    />
                                ))}
                            </Bar>
                            <Legend
                                verticalAlign="top"
                                height={36}
                                payload={[
                                    {
                                        value: chartConfig.optimizing.label,
                                        type: "square",
                                        color: chartConfig.optimizing.color,
                                    },
                                    {
                                        value: chartConfig.optimized.label,
                                        type: "square",
                                        color: chartConfig.optimized.color,
                                    },
                                ]}
                            />
                        </BarChart>
                    </ChartContainer>
                </CardContent>
                <CardFooter className="flex-col items-start gap-2">
                    <div className="flex items-center gap-2 font-medium">
                        {upliftData.length > 0 ? (
                            <>
                                Average uplift of{" "}
                                {(
                                    upliftData.reduce(
                                        (acc, curr) => acc + curr.uplift,
                                        0
                                    ) / upliftData.length
                                ).toFixed(1)}
                                % <TrendingUp className="h-4 w-4" />
                            </>
                        ) : (
                            "Average uplift not available yet"
                        )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                        Showing top {upliftData.length} products by revenue
                        uplift
                    </div>
                </CardFooter>
            </div>
        </Card>
    );
}
